import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';

import {FormInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ActionWithReasonButton} from 'src/components/save-with-reason/action-with-reason-button';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {CustomValidatedControl, ValidatedControl} from 'src/contexts/validation-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';

import Button from '@pb/kit-component-button';
import {Dialog} from '@pb/kit-component-dialog-v2';
import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import Loading from '@pb/kit-component-loading';
import {Textarea} from '@pb/kit-component-textarea-v2';
import {useShown} from '@pb/kit-hooks-use-shown';

import {ManageEntitlementsPage} from '../components/manage-entitlements-page';
import {RoleGrants} from '../role-grants/role-grants';
import {useAssignGrantsToRole} from '../role-grants/useAssignGrantsToRole';
import {useDischargeGrantsFromRole} from '../role-grants/useDischargeGrantsFromRole';
import {getEntitlementsReturnPath} from '../utils';

import {EntitlementRoleChangeLog} from './change-log';
import {
    ROLE_DESCRIPTION_CONTROL,
    ROLE_NAME_CONTROL,
} from './constants';
import {REMOVE_ROLE_MODAL_WIDTH, RemoveRoleModal} from './remove-role-modal';
import {useRoleGrants} from './useRoleGrants';
import {useUpdateRole} from './useUpdateRole';

import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {AccessRight} from 'src/hooks/access-rights/constants';


const EditRole = () => {
    const queryParams = useParams<{roleId: string}>();
    const roleId = queryParams.roleId || '';

    const {shown: deleteShown, onClick: openDelete, onClose: closeDelete} = useShown();

    const navigate = useNavigate();
    const {state: navigationState} = useLocation();
    const {editRoleOpen, editRoleSubmit} = useEventTracking();
    const {hasAccess} = useAdminAccessRight();
    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);
    const [busy, setBusy] = useState(false);

    const {
        isLoading: isRoleLoading,
        loadingError: roleLoadingError,
        data,
        setData,
        dataInitial,
        isChanged: isRoleChanged,
        saveChanges: updateRole,
        discardChanges: discardRoleChanges,
    } = useUpdateRole({roleId});

    const {
        isLoading: isGrantsLoading,
        loadingError: grantsLoadingError,
        data: grants,
        loadData: loadGrants,
    } = useRoleGrants({roleId, pageNumber: 1, pageSize: 250});

    const {
        data: assignedGrants,
        setData: setAssignedGrants,
        isChanged: isAssignedGrantsChanged,
        saveChanges: assignGrants,
        discardChanges: discardAssignedGrantsChanges,
    } = useAssignGrantsToRole();

    const {
        data: dischargedGrants,
        setData: setDischargedGrants,
        isChanged: isDischargedGrantsChanged,
        saveChanges: dischargeGrants,
        discardChanges: discardDischargedGrantsChanges,
    } = useDischargeGrantsFromRole();

    const {
        proceed,
        setIsChunkChanged,
        isCurrentChunkChanged,
    } = useConfirmationChunk({chunkName: 'update-role', onProceed: () => {
        setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        setTimeout(() => {
            loadGrants();
        }, 1500);
    }});

    const grantIds = useMemo(() => [...new Set([
        ...grants.grants
            .map(({id}) => id)
            .filter(externalId => !dischargedGrants.includes(externalId)),
        ...assignedGrants,
    ])], [grants, dischargedGrants, assignedGrants]);


    useEffect(() => {
        editRoleOpen();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setIsChunkChanged(isRoleChanged || isAssignedGrantsChanged || isDischargedGrantsChanged);
    }, [isRoleChanged, isAssignedGrantsChanged, isDischargedGrantsChanged, setIsChunkChanged]);

    const changeName = useCallback((name: string) => {
        setData(prev => ({...prev, name}));
    }, [setData]);

    const changeDescription = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setData(prev => ({...prev, description: event.target.value}));
    }, [setData]);

    const blurDescription = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
        setData(prev => ({...prev, description: event.currentTarget.value.trim()}));
    }, [setData]);

    const backToAllEntitlements = useCallback(() => {
        navigate(-1);
        editRoleSubmit({submit: false, success: true});
    }, [editRoleSubmit, navigate]);

    const saveChanges = useCallback(async (options?: ActionOptions) => {
        const calls = [];

        setBusy(true);
        if (isRoleChanged) {
            calls.push(await updateRole());
        }
        if (isAssignedGrantsChanged) {
            calls.push(await assignGrants({roleId, ...options}));
        }

        if (isDischargedGrantsChanged) {
            calls.push(await dischargeGrants({roleId, ...options}));
        }
        setBusy(false);

        const errors = [...new Set(calls.filter(Boolean))];
        if (errors.length) {
            setErrorMessage(errors.join('\n'));
        } else {
            proceed();
        }
        editRoleSubmit({success: !errors.length, submit: true});
        return errors.length ? errors.join('\n') : undefined;
    }, [
        isRoleChanged,
        isAssignedGrantsChanged,
        isDischargedGrantsChanged,
        updateRole,
        assignGrants,
        dischargeGrants,
        editRoleSubmit,
        proceed,
        roleId,
        setErrorMessage,
    ]);

    const discardChanges = useCallback(() => {
        discardRoleChanges();
        discardAssignedGrantsChanges();
        discardDischargedGrantsChanges();

        editRoleSubmit({submit: false, success: true});
        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
    }, [
        discardRoleChanges, discardAssignedGrantsChanges,
        discardDischargedGrantsChanges, editRoleSubmit, setSuccessMessage,
    ]);

    const addGrant = useCallback((resourceId: string) => {
        setDischargedGrants(prev => prev.filter(id => id !== resourceId));
        if (!grantIds.includes(resourceId)) {
            setAssignedGrants(prev => [...new Set([...prev, resourceId])]);
        }
    }, [grantIds, setDischargedGrants, setAssignedGrants]);

    const removeGrant = useCallback((resourceId: string) => {
        setAssignedGrants(prev => prev.filter(id => id !== resourceId));
        if (grantIds.includes(resourceId)) {
            setDischargedGrants(prev => [...new Set([...prev, resourceId])]);
        }
    }, [grantIds, setAssignedGrants, setDischargedGrants]);

    return (
        <Skeleton shown={isRoleLoading || isGrantsLoading} view={<Loading fast={true}/>}>
            <ServerResponseHandler
                serverError={roleLoadingError || grantsLoadingError}
                accessRight={ACCESS_RIGHTS.MANAGE_ENTITLEMENTS as AccessRight}
                sectionName="Edit Role">
                <ManageEntitlementsPage
                    title={dataInitial.name}
                    buttonsBlock={(
                        <Group>
                            {hasAccess(ACCESS_RIGHTS.DELETE_ENTITLEMENTS) ? (
                                <Button variant="Type.SECONDARY" colorVariant="ERROR" onClick={openDelete}>
                                    Delete
                                </Button>
                            ) : null}
                            <Button variant="Type.TERTIARY" onClick={backToAllEntitlements}>
                                Cancel
                            </Button>
                            <Button
                                variant="Type.SECONDARY"
                                disabled={!isCurrentChunkChanged}
                                onClick={discardChanges}>
                                Discard Changes
                            </Button>
                            <ActionWithReasonButton action="Save" legacyLabel="Save Changes" busy={busy} disabled={!isCurrentChunkChanged} onConfirm={saveChanges}/>
                        </Group>
                    )}>
                    <div className="pd-l">
                        <FormSection headline="Role Information">
                            <Group direction="v">
                                <CustomValidatedControl name={ROLE_NAME_CONTROL}>
                                    {error => (
                                        <FormInput
                                            value={data.name}
                                            width={300}
                                            maxLength={64}
                                            label={<RequiredMark label="Role Name"/>}
                                            placeholder="Role Name"
                                            onBlur={changeName}
                                            valid={error ? false : undefined}
                                            errorDescription={error}
                                        />
                                    )}
                                </CustomValidatedControl>
                                <FormInput
                                    value={data.id}
                                    readOnly={true}
                                    width={300}
                                    maxLength={64}
                                    label={<RequiredMark label="Role External ID"/>}
                                    placeholder="Role External ID"
                                />
                                <ValidatedControl name={ROLE_DESCRIPTION_CONTROL}>
                                    {invalid => (
                                        <WidthItem width={610}>
                                            <Label><RequiredMark label="Description"/></Label>
                                            <Textarea
                                                maxLength={255}
                                                color={invalid ? 'ERROR' : 'DEFAULT'}
                                                value={data.description}
                                                onBlur={blurDescription}
                                                onChange={changeDescription}
                                            />
                                        </WidthItem>
                                    )}
                                </ValidatedControl>
                            </Group>
                        </FormSection>
                        <RoleGrants
                            grantsIds={grantIds}
                            onAddGrant={addGrant}
                            onRemoveGrant={removeGrant}
                        />
                    </div>
                    <EntitlementRoleChangeLog roleId={roleId}/>
                    <Dialog
                        shown={deleteShown}
                        setShown={closeDelete}
                        wrapperProps={{style: {width: REMOVE_ROLE_MODAL_WIDTH}}}>
                        <RemoveRoleModal
                            roleId={roleId}
                            roleName={dataInitial.name}
                            onClose={closeDelete}
                            onDone={() => {
                                closeDelete();
                                setSuccessMessage('Role removed successfully');
                                navigate(getEntitlementsReturnPath(navigationState));
                            }}
                        />
                    </Dialog>
                </ManageEntitlementsPage>
            </ServerResponseHandler>
        </Skeleton>
    );
};

export const ValidateEditRole = () => (
    <ValidationContextProvider>
        <EditRole/>
    </ValidationContextProvider>
);
